'use client';

import { useEffect } from 'react';

interface Props {
  scripts: { id: string; code: string }[];
}

export function HeadScripts({ scripts }: Props) {
  useEffect(() => {
    // NOTE: We intentionally do NOT lock history.pushState / replaceState here.
    // React runs child effects before parent effects, so HeadScripts would lock
    // pushState BEFORE Next.js AppRouter (at the tree root) installs its own
    // navigation patch — causing "Cannot assign to read only property 'pushState'".
    // Osano's History-API monkey-patching is neutralised via the addEventListener
    // and Event.prototype guards below instead.

    // Osano CMP breaks SPA navigation in two ways:
    // 1. It replaces EventTarget.prototype.addEventListener with a wrapper that
    //    conditionally skips calling the original listener (React's handler)
    //    when consent hasn't been given.
    // 2. It registers its own capture-phase click listener that calls
    //    stopImmediatePropagation(), killing any remaining listeners.
    //
    // Fix A: lock addEventListener before Osano loads so it cannot install its
    //   wrapper — React's listeners are registered directly and always fire.
    // Fix B: patch Event.prototype so stopImmediatePropagation / stopPropagation
    //   are no-ops on link clicks — Osano's own listener can't kill propagation.
    if (!(window as any).__linkClickProtected) {
      (window as any).__linkClickProtected = true;

      // Fix A — lock addEventListener on both EventTarget and Node prototypes.
      // Osano installs its wrapper on whichever prototype has an own property;
      // DOM elements inherit from Node first so we must lock both.
      try {
        const origAEL = EventTarget.prototype.addEventListener;
        Object.defineProperty(EventTarget.prototype, 'addEventListener', {
          configurable: false, writable: false, value: origAEL,
        });
      } catch { /* already locked */ }
      try {
        const origAEL = EventTarget.prototype.addEventListener;
        Object.defineProperty(Node.prototype, 'addEventListener', {
          configurable: false, writable: false, value: origAEL,
        });
      } catch { /* already locked */ }

      // Fix B — lock fetch so Osano cannot block RSC requests.
      // Blogs/webinars use revalidate=0 and always need a live fetch;
      // if Osano intercepts fetch before consent, those navigations silently fail.
      try {
        const origFetch = window.fetch.bind(window);
        Object.defineProperty(window, 'fetch', {
          configurable: false, writable: false, value: origFetch,
        });
      } catch { /* already locked */ }

      // Fix C — neutralise preventDefault / stopPropagation / stopImmediatePropagation
      // on link clicks. Osano's capture-phase listener calls all three via w(event).
      // Next.js <Link> checks event.defaultPrevented before calling router.push —
      // if Osano sets it, navigation is silently skipped even if the event propagates.
      const isLinkClick = (e: Event) =>
        e.type === 'click' && !!(e.target as Element)?.closest?.('a[href]');

      try {
        const origPD = Event.prototype.preventDefault;
        Object.defineProperty(Event.prototype, 'preventDefault', {
          configurable: false, writable: false,
          value: function(this: Event) {
            if (isLinkClick(this)) return;
            origPD.call(this);
          },
        });
      } catch { /* already locked */ }

      try {
        const origSIP = Event.prototype.stopImmediatePropagation;
        Object.defineProperty(Event.prototype, 'stopImmediatePropagation', {
          configurable: false, writable: false,
          value: function(this: Event) {
            if (isLinkClick(this)) return;
            origSIP.call(this);
          },
        });
      } catch { /* already locked */ }

      try {
        const origSP = Event.prototype.stopPropagation;
        Object.defineProperty(Event.prototype, 'stopPropagation', {
          configurable: false, writable: false,
          value: function(this: Event) {
            if (isLinkClick(this)) return;
            origSP.call(this);
          },
        });
      } catch { /* already locked */ }
    }

    scripts.forEach(s => {
      if (document.getElementById(s.id)) return;

      // Carry over attributes from the stored <script ...> tag (src, async,
      // data-osano, data-token, etc.) — these used to be silently discarded,
      // which broke vendor snippets that rely on attributes rather than
      // inline code, e.g. Osano's native data-osano="CATEGORY" blocking.
      const openTagMatch = s.code.match(/^\s*<script([^>]*)>/i);
      const attrsStr = openTagMatch ? openTagMatch[1] : '';
      const inner = s.code
        .replace(/^\s*<script[^>]*>\s*/i, '')
        .replace(/\s*<\/script>\s*$/i, '')
        .trim();

      const el = document.createElement('script');
      el.id = s.id;

      const attrRegex = /([a-zA-Z0-9_-]+)(?:=["']([^"']*)["'])?/g;
      let match;
      while ((match = attrRegex.exec(attrsStr)) !== null) {
        const [, name, value] = match;
        if (name.toLowerCase() === 'id') continue; // we always set our own id above
        el.setAttribute(name, value ?? '');
      }

      if (inner) el.textContent = inner;

      document.head.appendChild(el);
    });

  }, [scripts]);

  return null;
}
